import React, { FC, useEffect } from "react";
import ParcelIcon from "components/Icon/icons/parcel.svg";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { format } from "date-fns";
import { List } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import ImageViewer2 from "../../../../components/ImageViewer2/ImageViewer2";
import { getContainerDetail } from "../../../../reduxFolder/reducers/containers";
import {
  containersSelectDetailed,
  containersSelectDetailedLoading,
} from "../../../../reduxFolder/selectors/containersSelectors";
import { authSelectUnits } from "../../../../reduxFolder/selectors/globalSelectors";
import { ParcelLogo } from "../../../../styles/parcelsStyles";
import { getFnsLocale } from "../../../../translations/date-fns-locale";
import { toastResponseError } from "../../../../utils/responseMessageHelper";
import Tab from "../../../RightPanel/components/Tab";
import TabsView from "../../../RightPanel/components/TabsView";
import ContainerShipments from "../ContainerShipments/ContainerShipments";
import {
  Container,
  ContentContainer,
  ImageSection,
  ParcelInfoContainer,
  ParcelInfoKey,
  ParcelInfoValue,
  Spinner,
  SpinnerSection,
  Title,
} from "./ContainerDetailedPanel.styles";

interface ContainerDetailedPanelProps {
  isOpen: boolean;
  units: string;
  panelData: any;
  detailedContainer: any;
  isDetailedLoading: boolean;
  getContainerDetail: (id: number) => Promise<void>;
}

const ContainerDetailedPanel: FC<ContainerDetailedPanelProps> = ({
  isOpen,
  panelData,
  detailedContainer,
  units,
  getContainerDetail,
  isDetailedLoading,
}) => {
  const { i18n, t } = useTranslation("common");
  const locale = getFnsLocale(i18n.language);
  const containerId = panelData.get("containerId");
  const container = detailedContainer.get(containerId);
  const showContainer = !!container;

  useEffect(() => {
    if (isOpen && containerId) {
      getContainerDetail(containerId).catch(toastResponseError);
    }
  }, [isOpen, containerId, getContainerDetail]);

  const created =
    showContainer && container.get("created_at")
      ? format(new Date(container.get("created_at")), "dd MMMM, y", { locale })
      : "";
  const weight = showContainer ? container.get("weight") : null;
  const pictures = showContainer ? container.get("pictures", List()) : List();

  return (
    <ContentWrapper key={containerId}>
      <Heading>
        <ParcelLogo image={ParcelIcon} />
        <Title>
          {showContainer ? container.get("sku") : panelData.get("sku")}
        </Title>
      </Heading>
      <TabsView>
        <Tab title={t("parcels.details")}>
          <ContentContainer>
            <Container>
              {!showContainer && (
                <SpinnerSection>
                  <Spinner isActive={isDetailedLoading} />
                </SpinnerSection>
              )}
              {showContainer && (
                <>
                  <ImageSection>
                    <ImageViewer2
                      images={pictures.size ? pictures.toJS() : []}
                    />
                  </ImageSection>
                  <ParcelInfoContainer>
                    <div>
                      <ParcelInfoKey>
                        {t("containers.created")}:{" "}
                      </ParcelInfoKey>
                      <ParcelInfoValue>{created}</ParcelInfoValue>
                    </div>
                    <div>
                      <ParcelInfoKey>{`${t("containers.weight")}: `}</ParcelInfoKey>
                      <ParcelInfoValue>
                        {weight != null
                          ? `${Number(weight).toFixed(2)} ${t(`units.${units}.primaryShort`)}`
                          : "—"}
                      </ParcelInfoValue>
                    </div>
                  </ParcelInfoContainer>
                  <ParcelInfoContainer>
                    <div>
                      <ParcelInfoKey>{t("common.state")}: </ParcelInfoKey>
                      <ParcelInfoValue>
                        {t(`containers.state.${container.get("state")}`)}
                      </ParcelInfoValue>
                    </div>
                    <div>
                      <ParcelInfoKey>
                        {t("containers.dimensions")}:{" "}
                      </ParcelInfoKey>
                      <ParcelInfoValue>
                        {container.get("width")} x {container.get("height")} x{" "}
                        {container.get("length")}{" "}
                        {t(`units.${units}.secondaryShort`)}
                      </ParcelInfoValue>
                    </div>
                  </ParcelInfoContainer>
                  <ContainerShipments container={container} />
                </>
              )}
            </Container>
          </ContentContainer>
        </Tab>
      </TabsView>
    </ContentWrapper>
  );
};

const withConnect = connect(
  createStructuredSelector({
    detailedContainer: containersSelectDetailed,
    isDetailedLoading: containersSelectDetailedLoading,
    units: authSelectUnits,
  }),
  { getContainerDetail },
);

export default withConnect(ContainerDetailedPanel);
